/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { Field } from '@src/app/shared/field/Field';
import { ContactTypes, EntityType, EntityTypes, MAIL_STATUS, Person } from '@src/app/model/person';
import { PersonUnitRole } from '@src/app/model/person-unit-role';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { STD_ANIMATION } from '@src/app/shared/std-animation';
import { UnitCategoryService } from '@src/app/modules/unit/unit-type-page/unit-category.service';
import { UnitTypeService } from '@src/app/modules/unit/unit-type-page/unit-type.service';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { required, uniqueGrid } from '@src/app/shared/validators';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { of } from 'rxjs';
import { FormEmailComponent } from '@src/app/shared/form/form-email/form-email.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { ButtonField } from '@src/app/shared/field/ButtonField';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { uuid } from '@src/app/model/abstract-object';
import { DateHelper } from '@src/app/shared/dateHelper';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { Role } from '@src/app/model/role';
import { RoleService } from '@src/app/pages/role-page/role.service';
import { ActionGridComponent } from '@src/app/shared/action/action-grid.component';
import { SendEmailFormAction } from '@src/app/shared/action/send-email-form-action';
import { MemoField } from '@src/app/shared/memos/MemoField';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { MailService } from '@src/app/shared/mail-service';
import { MergePersonAction } from '@src/app/pages/person-page/merge-person-action';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { PersonRequestAction } from '@src/app/pages/person-page/person-request-action';
import { AssociateContactAction } from '@src/app/pages/person-page/associate-contact-action';
import { FormPhoneComponent } from '@src/app/shared/form/form-phone/form-phone.component';
import { UpdateEmailAction } from '@src/app/pages/person-page/update-email-action';
import { FieldSetDialogComponent, FieldSetDialogOptions } from '@src/app/shared/dialogs/field-set-dialog/field-set-dialog.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';

@Component({
    selector: 'app-person-page',
    templateUrl: './person-page.component.html',
    styleUrls: ['./person-page.component.scss'],
    animations: [STD_ANIMATION],
    imports: [FormPageComponent]
})
export class PersonPageComponent extends AbstractPageComponent {
    dataSvc = inject(PersonService);
    roleService = inject(RoleService);
    typeService = inject(UnitTypeService);
    categoryService = inject(UnitCategoryService);
    dialog = inject(MatDialog);
    protected unitSvc = inject(UnitService);
    private mailSvc = inject(MailService);
    dialogRef = inject<MatDialogRef<PersonPageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    help = $localize `
    Contacts are the people and organisations. 
    The owners of the individual units and the residents who live in the development.
    Suppliers, Craftsmen and Professionals that have business with the OMC can all be tracked as contacts.
    `
    unclaimedHint = "When a user signs up with this email address, " +
        "they will be automatically matched to this person record and granted access to the OMC";

    claimedHint = "This person record is tied to a verified user account, the email address can no longer be changed";
    emailField = FormEmailComponent.make('Email', 'email', { formColumn: 2, hint: this.unclaimedHint });

    getUnitField() {
        if (!this.dialogRef) {
            return FormNavLinkComponent.makeDetail('Unit', 'unit.name', 'unitId', UnitPageNavRoute, true);
        } else {
            return FormTextComponent.make('Unit', 'unit.name', { readonly: true, sendServer: false });
        }
    }

    mailItemGrid = this.mailSvc.getMailItemGrid(this.dataSvc, 'recipient');

    forceTeamId: uuid;

    associatedField = new GridField({
        field: { value: 'associatedContacts', cellOpts: { heading: 'Associated Contacts' }, formColumn: 2, formRow: 2, sendServer: false },
        rowFactory: this.associatedRowFactory.bind(this),
    });

    associatedRowFactory(o: Person) {
        const fields = [
            FormButtonComponent.makeTextButton('Person Name', 'fullName', (cell) => {
                this.editGridContact(o, cell);
            }, {readonly: true}),
            FormPhoneComponent.make('Telephone', 'phone', { readonly: true }),
            FormEmailComponent.make('Email', 'email', { readonly: true }),
            FormTextAreaComponent.make('Notes', 'notes', {readonly: true})
        ];

        return fields;
    }

    unitRoleList: Role[] = [];

    editPrimaryContact() {
        const id = this.page.form.focusItem['primaryContactId'];
        const forceTeamId = this.page.form.focusItem['teamId'];
        const popDialogRef = this.dialog.open(PersonPageComponent, {data: {id: id, forceTeamId}});
        popDialogRef.afterClosed().subscribe(person => {
            if (person) {
                this.primaryContact.control.setValue(person.fullName, {emitEvent: false});
                this.primaryContact.control.markAsPristine();
                this.primaryContact.control.markAsTouched();
            }
        })
    }

    editGridContact(p: Person, cell: AppFormControl) {
        const popDialogRef = this.dialog.open(PersonPageComponent, {data: {id: p.id, forceTeamId: p.teamId}});
        popDialogRef.afterClosed().subscribe(person => {
            if (person) {
                cell.getRow().refreshFrom(p);
            }
        })
    }

    calcUnitIndicatorField(f: ButtonField, pur: PersonUnitRole) {
        const futureMember = pur.startDate && !DateHelper.beforeToday(pur.startDate);
        const pastByDate = pur.endDate && DateHelper.beforeToday(pur.endDate);
        const currentByDate = !futureMember && !pastByDate;
        const ownerChangeCurrent = !pur.ownerChangeId;
        const mismatch = !futureMember && ownerChangeCurrent !== currentByDate;

        if (futureMember) {
            f.btnOpts.iconStyle = 'font-size: 24px; color:orange; padding-left:0px';
            f.toolTip = $localize`This unit relationship will start in the future`;
            return 'hourglass_top';
        } else if (mismatch) {
            f.btnOpts.iconStyle = 'font-size: 24px; color:red; padding-left:0px';
            f.toolTip = $localize`Warning: ownership record conflicts with start/end dates`;
            return 'warning';
        } else if (!ownerChangeCurrent) {
            f.btnOpts.iconStyle = 'font-size: 24px; color:red; padding-left:0px';
            f.toolTip = $localize`This is a past relationship with the unit`;
            return 'block';
        } else {
            f.btnOpts.iconStyle = 'font-size: 24px; color:green; padding-left:0px';
            f.toolTip = $localize`This is a current relationship with the unit`;
            return 'check';
        }
    }

    unitRowFactory(o: PersonUnitRole) {
        const roleField = FormPicklistComponent.make('Role', 'roleId', 'role',
            { items: [] },
            { cellOpts: { width: '20%' }, validators: [required], readonly: true }
        );
        if (o?.id) {
            roleField.setPicklistItems(this.roleList);
        } else {
            roleField.setPicklistItems(this.roleList.filter(o => o.unitLinked))
        }

        return [
            FieldMaker.id({ visible: Field.noShow }),
            FormTextComponent.make('unitId', 'unitId', { visible: Field.noShow }),
            FormButtonComponent.make('', '', {
                name: 'statusField', type: 'icon', sendServer: false, label: '', readonly: true,
                cellOpts: { heading: '', width: '1%', style: 'padding: 0px 0px 0px 0px;' },
                calculateValue: (_o, f: ButtonField) => this.calcUnitIndicatorField(f, o),
            }),
            this.getUnitField(),
            FormButtonComponent.makeTextButton('Role', 'role.name', () => {
                this.editRole(o);
            }),
            FormDateComponent.make('From', 'startDate', { cellOpts: { width: '2%' }, readonly: true }),
            FormDateComponent.make('Until', 'endDate', { cellOpts: { width: '2%' }, readonly: true }),
            FieldMaker.notes({ cellOpts: { width: '50%' }, readonly: true }),
        ]

    }

    personUnitRoleField = FieldMaker.idHolder('personUnits');

    unitsField = new GridField({
        field: { value: 'units', cellOpts: { heading: 'Units' }, formColumn: 1, formRow: 2, sendServer: false, readonly: true },
        rowFactory: this.unitRowFactory.bind(this),
    });

    roleGrid: GridField = new GridField({
        field: { value: 'roles', cellOpts: { heading: 'Roles' }, formColumn: 6, sendServer: false, visible: Field.formOnly },
        rowFactory: this.roleGridRow.bind(this),
        gridValidator: [uniqueGrid('roleId')],
        objFactory: () => of(new PersonUnitRole()),
    });

    roleList: Role[] = [];

    openRequestsLink = FormNavLinkComponent.make('Open Requests',
        ServiceRequestPageNavRoute, o => ({ personId: o?.id, _sort: 'createdAt' }),
        { formColumn: 5, visible: Field.formOnly, value: 'open_requests_count', cellOpts: { style: 'text-align: center; max-width: 3em' } }
    );

    requestsLink = FormNavLinkComponent.make('Total Requests',
        ServiceRequestPageNavRoute, o => ({ personId: o?.id, _sort: 'createdAt' }),
        { formColumn: 5, value: 'requests_count', visible: Field.formOnly, cellOpts: { style: 'text-align: center; max-width: 3em' } }
    );

    actionGrid = ActionGridComponent.make('Person').setHelp($localize `
        The actions that have been recorded or are planned for this contact.
    `);
    memoField = new MemoField(this.dataSvc).setHelp($localize `
        Useful notes and comments relating to this this contact. 
        Remember, under GDPR, at some point you may need to share such comments with the contact
        under a freedom of information request, so please be sensitive in your comments.
    `);

    attachmentGrid = AttachmentGridComponent.make(this.dataSvc).setHelp($localize `
        Files relating to this contact. 
        Once attached here, files will can be attached to emails to this contact.
    `);

    noMailField = FormPicklistComponent.make('Mail Status', 'mailStatus', null, {
        items: MAIL_STATUS.items,
    }, {formColumn: 2});

    entityField = FormPicklistComponent.make('Entity Type', 'entityType', null, {
        items: EntityTypes.items,
    }, {formColumn: 3});

    contactType = FormPicklistComponent.make('Contact Type', 'contactType', null, {
        items: ContactTypes.omcSelectables(),
    }, {formColumn: 3});

    primaryContact = FormButtonComponent.makeTextButton('Primary Contact', 'primaryContactName', this.editPrimaryContact.bind(this)).override({formColumn: 3});

    titleField = Person.getTitleField().override({ formColumn: 1 });
    firstNameField = FormTextComponent.make('First Name', 'firstName', { formColumn: 1 });
    lastNameField = FormTextComponent.make('Last Name', 'lastName', { formColumn: 1 }); /* validators: [required] */

    fields = [
        FormTextComponent.make('Keyword Search', 'keyword', { visible: Field.noShow, sendServer: false }),
        FieldMaker.id(),
        FieldMaker.rev(),
        this.titleField,
        this.firstNameField,
        this.lastNameField,
        FormPhoneComponent.make('Telephone', 'phone', { formColumn: 2 }),
        FormPicklistComponent.make('Current Member', 'currentMember', null, {items: [{id:1, name: 'Yes'}]}, {
            visible: Field.noShow, sendServer: false,
        }),        
        this.emailField,
        this.noMailField,
        this.entityField, this.contactType, this.primaryContact,
        FormTextAreaComponent.make('Mailing Address', 'address', { formColumn: 4 }),
        FormTextComponent.make('User Name', 'user.name', { disable: true, sendServer: false, formColumn: 4, cellOpts: { width: '10%' } }),
        FieldMaker.notes({ formColumn: 5 }),
        this.openRequestsLink,
        this.requestsLink,
        this.unitsField,
        this.roleGrid,
        this.personUnitRoleField,
        this.associatedField,
        FieldMaker.idHolder('mergeId'),
    ]

    config = new FormConfig({
        navRoute: PersonPageNavRoute,
        docPath: 'omc-community/managers/people',
        title: $localize`Contact`,
        help: this.help,
        fieldSet: new FieldSet({
            fields: this.fields,
            formLayout: [
                { cells: [{ width: '5%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }] },
                { cells: [{ colspan: 3, pageTab: 'yes' }, { colspan: 3, pageTab: 'yes' }] }
            ],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newEntry.bind(this),
        beforeEdit: this.beforeEdit.bind(this),
        beforeSave: this.beforeSave.bind(this),
        actions: [new UpdateEmailAction(), new AssociateContactAction(), new PersonRequestAction(), new SendEmailFormAction(), new MergePersonAction()],
        tabFields: [this.actionGrid, this.memoField, this.mailItemGrid, this.attachmentGrid],
        setTeam: (teamId) => {
            this.forceTeamId = teamId;
            this.roleService.get(false, null, teamId).subscribe(roles => {
                this.roleList.length = 0;
                this.unitRoleList.length = 0;
                roles.forEach((r: Role) => {
                    this.roleList.push(r);
                    if (r.unitLinked) {
                        this.unitRoleList.push(r);
                    }
                });
            });
        }
    });

    roleGridRow(o: PersonUnitRole) {
        const f = FormPicklistComponent.make('Role', 'roleId', 'role', { items: [] }, { validators: [required] });

        if (o && o.id) {
            f.override({ readonly: true });
            f.setPicklistItems(this.roleList);
        } else if (o) {
            f.setPicklistItems(this.roleList.filter(o => !o.unitLinked));
        }

        const deleter = FieldMaker.deleteGridRow();
        if (o?.unitId) {
            deleter.override({ disable: true });
        }
        return [FieldMaker.idHolder('id'), f, deleter];
    }

    newEntry() {
        this.emailField.defaultReadonly = false;
        this.emailField.hint = this.unclaimedHint;
        return of(new Person());
    }

    beforeSave() {
        const pur = [];
        const units = this.unitsField.getFormValue();
        units.forEach(u => pur.push(u));
        const roles = this.roleGrid.getFormValue();
        roles.forEach(u => pur.push(u));
        this.personUnitRoleField.control.setValue(pur);
    }

    toggleNameFields(et: EntityType) {
        if (et === EntityTypes.organisation && !this.firstNameField.getFormValue() && !this.firstNameField.getFormValue()) {
            this.firstNameField.visible = Field.noShow;
            this.titleField.visible = Field.noShow;
            this.lastNameField.label = $localize `Organisation Name`;
            console.log(et);
        } else {
            console.log('no', et);
            this.firstNameField.visible = Field.showAll;
            this.titleField.visible = Field.showAll;
            this.lastNameField.label = $localize `Last Name`;
        }
    }

    beforeEdit(o: Person) {
        if (!o) {
            o = new Person();
        }

        if (o.userId) {
            this.emailField.hint = this.claimedHint;
            this.emailField.defaultReadonly = true;
        } else {
            this.emailField.hint = this.unclaimedHint;
            this.emailField.defaultReadonly = false;
        }
        if (o.primaryContactId) {
            this.primaryContact.visible = Field.showAll;
        } else {
            this.primaryContact.visible = Field.noShow;
        }

        this.toggleNameFields(o.entityType);

        o.roles = [];
        o.units = [];
        o.personUnits.forEach(pur => {
            if (pur.unit) {
                o.units.push(pur);
            } else {
                o.roles.push(pur);
            }
        })
        return o;
    }

    saveRoleDetails(e: PersonUnitRole) {
        return this.dataSvc.updateRole(e)
    }

    editRole(pur: PersonUnitRole) {
        const fieldSet = new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.idHolder('unitId'),
                FieldMaker.idHolder('personId'),
                FieldMaker.idHolder('teamId'),
                FormPicklistComponent.make('Role', 'roleId', 'role',
                    { items: this.unitRoleList, refreshes: ['roleId'] },
                    { cellOpts: { minWidth: '10em' }, validators: [required] },
                ),
                FormDateComponent.make('From', 'startDate'),
                FormDateComponent.make('To', 'endDate'),
                FormTextAreaComponent.make('Notes', 'notes')

            ],
            formLayout: LAYOUT_OPTIONS.simpleDialog
        })
        fieldSet.setValue(pur)
        const data = {
            fieldSet,
            saveFn: this.saveRoleDetails.bind(this),
            saveText: $localize`Save`,
            title: $localize`${(this.page.form.focusItem as Person).fullName} in ${pur?.unit.name}`,
        } as FieldSetDialogOptions

        this.dialog.open(FieldSetDialogComponent, { data }).afterClosed().subscribe(update => {
            console.log(update);
            if (update) { // Full reloads - the ownerOccupier might have changed.
                this.page.form.reloadContent(update);
            }
        })
    }    

    plMapper(itemPicked): PersonUnitRole {
        if (itemPicked) {
            const newItem = new PersonUnitRole();
            newItem.unitId = itemPicked.id;
            newItem.unit = itemPicked;
            return newItem;
        } else {
            return null;
        }
    }
}
